import { z } from 'zod'
import { ProviderAffiliationStatusValueSchema } from '@/queries/providers/affiliations/schema.ts'

export const DistributionStatusSchema = z.union([
  z.literal('pending'),
  z.literal('processing'),
  z.literal('completed'),
  z.literal('failed'),
])

// New schema based on program-purse.resource.json
const GlobalGradeLevelSchema = z.object({
  id: z.number(),
  status: z.number(),
  name: z.string(),
  created_at: z.union([z.string(), z.null()]),
  updated_at: z.union([z.string(), z.null()]),
})

const ProgramPurseGradeDistributionSchema = z.object({
  id: z.number().min(0),
  program_purse_id: z.number().min(0),
  grade_level_id: z.number().min(0),
  grade_level_name: z.string(),
  amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  global_grade_level: GlobalGradeLevelSchema,
})

const ProgramPurseFixedDistributionSchema = z.object({
  id: z.number().min(0),
  program_purse_id: z.number().min(0),
  base_amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
})

const ProgramPurseEducationDistributionSchema = z.object({
  id: z.number().min(0),
  program_purse_id: z.number().min(0),
  education_type_id: z.number().min(0),
  education_type_code: z.string(),
  education_type_name: z.string(),
  base_amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
})

export const ModifierCodeSchema = z.enum(['special_education', 'deans_list'])

const DistributionModifierSchema = z.object({
  id: z.number(),
  base_amount: z.number().min(0),
  modifier_type_id: z.number(),
  modifier_code: ModifierCodeSchema,
  modifier_name: z.string(),
  grade_level_id: z.number(),
  grade_level_name: z.string(),
  is_active: z.boolean(),
})

const ProgramPurseGradeDistributionsSchema = z.array(
  ProgramPurseGradeDistributionSchema
)

const ProgramPurseDistributionsRulesSchema = z.object({
  grade_distributions: ProgramPurseGradeDistributionsSchema,
  fixed_distribution: ProgramPurseFixedDistributionSchema,
  education_distributions: z.array(ProgramPurseEducationDistributionSchema),
  distribution_modifiers: z.array(DistributionModifierSchema),
})

export const AwardingMethodCode = z.enum([
  'fixed_amount',
  'grade_level',
  'education_type',
])

export const ProgramPurseResourceSchema = z.object({
  id: z.number().min(0),
  program_id: z.number().min(0),
  name: z.string(),
  status: z.number(),
  source_type: z.number(),
  sub_source_type: z.number(),
  external_provider_id: z.string().nullable(),
  total: z.number(),
  balance_available: z.number(),
  balance_distribuited: z.number(), // Note: key name matches API field
  can_distribute_funds_automatically: z.boolean(),
  affiliation_restriction_enabled: z.boolean(),
  bank_account_id: z.number().nullable(),
  awarding_method_code: AwardingMethodCode.nullable(),
  awarding_method_name: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  distributions_rules: ProgramPurseDistributionsRulesSchema,
  academic_term_type_id: z.number().nullable(),
})

const GlobalEducationTypeSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  created_at: z.string(),
})

const ProgramPurseEducationDistributionListSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  education_type_id: z.number(),
  education_awarding_type_code: z.string(),
  education_awarding_type_name: z.string(),
  base_amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  education_awarding_type: GlobalEducationTypeSchema,
})

export const PurseSchema = z.object({
  id: z.number(),
  status: z.number(),
  program_id: z.number(),
  source_type: z.number(),
  sub_source_type: z.number(),
  external_provider_id: z.string().nullable(),
  name: z.string(),
  total: z.number(),
  balance_available: z.number(),
  balance_distribuited: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  can_distribute_funds_automatically: z.boolean(),
  grade_distributions: z.array(ProgramPurseGradeDistributionSchema).optional(),
  education_distributions: z
    .array(ProgramPurseEducationDistributionListSchema)
    .optional(),
  fixed_distribution: ProgramPurseFixedDistributionSchema.nullable().optional(),
  awarding_method_code: AwardingMethodCode.nullable(),
  awarding_method_name: z.string().nullable(),
  academic_term_type_id: z.number().nullable().optional(),
  bank_account_id: z.number().nullable().optional(),
  // Students ServiceNow has put in Awarded status on this purse, waiting to be funded.
  // Optional so an API without the field yet leaves the count unknown rather than failing
  // the whole purse list; `undefined` must not be read as "zero awarded students".
  awarded_students_count: z.number().optional(),
})

export const ProgramPurseListSchema = z.array(PurseSchema)

export type AwardingMethodCode = z.infer<typeof AwardingMethodCode>
export type ProgramPurseResource = z.infer<typeof ProgramPurseResourceSchema>
export type ModifierCode = z.infer<typeof ModifierCodeSchema>
export type DistributionModifier = z.infer<typeof DistributionModifierSchema>
export type Purse = z.infer<typeof PurseSchema>
export type ProgramPurseList = z.infer<typeof ProgramPurseListSchema>

// Distribution Summary Schemas
const DistributionSchema = z.object({
  distribution_id: z.number(),
  program_purse_id: z.number(),
  status: DistributionStatusSchema,
  awarded_students_count: z.number(),
  started_at: z.string().datetime().nullable(),
  started_by: z.string().nullable(),
  completed_at: z.string().datetime().nullable(),
  download_url: z.string(),
})

const GetPurseDistributionsResponseSchema = z.object({
  program_id: z.number().int(),
  program_purse_id: z.number().int(),
  distributions: z.array(DistributionSchema),
})

export type DistributionStatus = z.infer<typeof DistributionStatusSchema>
export type GetPurseDistributionsResponse = z.infer<
  typeof GetPurseDistributionsResponseSchema
>
export type PurseDistribution = z.infer<typeof DistributionSchema>

export { GetPurseDistributionsResponseSchema, DistributionSchema }

// Accepts the numeric strings Laravel can emit for decimals, but null, '' and other nullish
// values must fail the parse: coercing them to 0 would render a funding total of $0.00 and read
// as "nothing to fund" when the real total is simply unknown. The trailing `finite` pipe also
// rejects a digit string long enough to overflow to Infinity, which the regex alone allows.
const NumericTotalSchema = z
  .union([
    z.number(),
    z.string().regex(/^-?\d+(\.\d+)?$/, 'Expected a numeric value'),
  ])
  .transform(Number)
  .pipe(z.number().finite())

// The API reports this as `(int) COUNT(*)`, so anything fractional or negative is a bad payload
// rather than a count to render.
const CountSchema = NumericTotalSchema.pipe(z.number().int().nonnegative())

// `plan_preview` now reports the students ServiceNow put in Awarded status on the purse and the
// sum of the amounts it supplied for them. It no longer derives anything from distribution rules.
export const AwardedStudentsPreviewSchema = z.object({
  total_students: CountSchema,
  total_amount: NumericTotalSchema,
})

export type AwardedStudentsPreview = z.infer<
  typeof AwardedStudentsPreviewSchema
>

// Student wallet purses credited by `fund_awarded_students`. The API returns the full
// wallet models, we only rely on the identity and resulting balance.
const FundedStudentPurseSchema = z
  .object({
    id: z.number().optional(),
    student_id: z.number().optional(),
    program_purse_id: z.number().optional(),
    balance_available: z.coerce.number().optional(),
  })
  .passthrough()

export const FundAwardedStudentsResponseSchema = z.object({
  total_awarded: z.coerce.number(),
  balance_available: z.coerce.number(),
  student_purses: z.array(FundedStudentPurseSchema),
})

export type FundedStudentPurse = z.infer<typeof FundedStudentPurseSchema>
export type FundAwardedStudentsResponse = z.infer<
  typeof FundAwardedStudentsResponseSchema
>

const ProgramPurseAffiliationSchema = z.object({
  id: z.number(),
  name: z.string(),
  logo: z.string().nullable(),
  status: ProviderAffiliationStatusValueSchema,
  description: z.string().nullable(),
})

export const ProgramPurseAffiliationsResponseSchema = z.object({
  program_purse_id: z.number(),
  affiliation_restriction_enabled: z.boolean(),
  affiliations: z.array(ProgramPurseAffiliationSchema),
})

export type ProgramPurseAffiliation = z.infer<
  typeof ProgramPurseAffiliationSchema
>

export type ProgramPurseAffiliationsResponse = z.infer<
  typeof ProgramPurseAffiliationsResponseSchema
>
